import { format } from "date-fns";
import { BookingDetailsDTO, OrderDetailsDTO } from "./bookingService";

import { useEffect, useState, useRef } from "react";

import deleteBooking from "./deleteBooking";
import updateBooking from "./updateBooking";
import { useToast } from "../../Contexts/ToastContext";

import axios from "axios"; // Import axios for fetching available chalets

const apiUrl = process.env.REACT_APP_API_URL;

interface BookingUpdateRequest {
  bookingDescription?: string;
  guestNames?: string[]; // Add guestNames to the request
  arrivalDate?: string;
  departureDate?: string;
  totalPrice?: number;
  chaletType?: "big" | "small"; // Add chaletType property
  chaletId?: number; // Add chaletId property
}

interface ChaletDTO {
  chaletId: number; // Correct the property name
  chaletType: number; // Update to number to handle numeric values
  isBlocked: boolean;
  blockedDates: BlockedDateRangeDTO[];
}

interface BlockedDateRangeDTO {
  id: number;
  startDate: Date;
  endDate: Date;
  chaletId: number;
  reasonDescription?: string;
}

interface AvailableChaletsProps {
  chaletType: "big" | "small";
  startDate: string | null;
  endDate: string | null;
  onSelectChalet: (id: number) => void;
  selectedChaletId: number | null; // Add selectedChaletId prop
}

interface NewManageBookingDetailsProps {
  order: OrderDetailsDTO;
  booking?: BookingDetailsDTO;
  onEdit?: (booking: BookingDetailsDTO) => void;
  fetchBookings: () => void; // Add fetchBookings to the props
  onClose?: () => void;
  onUpdate?: () => void; // Add onUpdate to the props
}

const AvailableChalets: React.FC<AvailableChaletsProps> = ({
  chaletType,
  startDate,
  endDate,
  onSelectChalet,
  selectedChaletId,
}) => {
  const [chalets, setChalets] = useState<ChaletDTO[]>([]);
  const [availabilityInfo, setAvailabilityInfo] = useState<string>("");

  useEffect(() => {
    if (!chaletType || !startDate || !endDate) return; // Ensure chalet options are only fetched after selecting a chalet type

    const fetchChalets = async () => {
      try {
        // Use the new calendar-availability endpoint that properly deduplicates by chaletId
        const response = await axios.get(`${apiUrl}/PackageBooking/calendar-availability`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          params: {
            startDate,
            endDate,
          },
        });
        
        const data = response.data as any;
        console.log("Calendar availability response (CalendarViewManageBookingDetails):", data);
        
        if (data && data.availabilityByType) {
          // Find the chalet type the user selected
          const chaletTypeNormalized = chaletType.toLowerCase();
          const typeData = data.availabilityByType.find((a: any) => {
            const backendType = String(a.chaletType).toLowerCase();
            return backendType === chaletTypeNormalized || 
                   (chaletTypeNormalized === "big" && (backendType === "0" || backendType === "big")) ||
                   (chaletTypeNormalized === "small" && (backendType === "1" || backendType === "small"));
          });
          
          if (typeData && typeData.availableChaletIds) {
            // Convert availableChaletIds to ChaletDTO format
            const availableChaletDTOs: ChaletDTO[] = typeData.availableChaletIds.map((id: number) => ({
              chaletId: id,
              chaletType: chaletTypeNormalized === "big" ? 0 : 1,
              isBlocked: false,
              blockedDates: [],
            }));
            
            setChalets(availableChaletDTOs);
            setAvailabilityInfo(`${typeData.availableChalets} of ${typeData.totalChalets} available`);
          } else {
            setChalets([]);
            setAvailabilityInfo("No chalets available");
          }
        } else {
          setChalets([]);
        }
      } catch (error) {
        console.error("Error fetching available chalets:", error);
        setChalets([]);
      }
    };

    fetchChalets();
  }, [chaletType, startDate, endDate]);

  const getChaletTypeLabel = (type: number) => {
    return type === 0 ? "Big" : "Small";
  };

  return (
    <div>
      <h5>Available Chalets {availabilityInfo && <small className="text-muted">({availabilityInfo})</small>}</h5>
      {chalets.length === 0 ? (
        <p className="text-warning">No chalets available for the selected dates and type.</p>
      ) : (
        <ul>
          {chalets.map((chalet) => (
            <li key={chalet.chaletId}>
              <button
                onClick={() => onSelectChalet(chalet.chaletId)}
                style={{
                  backgroundColor:
                    chalet.chaletId === selectedChaletId ? "lightblue" : "white",
                }}
              >
                Chalet {chalet.chaletId} ({getChaletTypeLabel(chalet.chaletType)})
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CalendarViewManageBookingDetails: React.FC<
  NewManageBookingDetailsProps
> = ({
  order,
  booking,
  onEdit,
  onClose,
  fetchBookings, // Add fetchBookings to the props
  onUpdate, // Add onUpdate to the props
}) => {
  const { showToast } = useToast();
  const [editingBooking, setEditingBooking] =
    useState<BookingDetailsDTO | null>(null);
  const [arrivalDate, setArrivalDate] = useState<string | null>(null);
  const [departureDate, setDepartureDate] = useState<string | null>(null);

  const [guestNamesInput, setGuestNamesInput] = useState<string>("");
  // Dedicated state for updated guest names in view mode
  const [updatedGuestNames, setUpdatedGuestNames] = useState<string | null>(null);
  // Reset guestNamesInput when editingBooking changes (enter/exit edit mode)
  useEffect(() => {
    if (editingBooking) {
      setGuestNamesInput(
        editingBooking.guestNames ? editingBooking.guestNames.join(", ") : ""
      );
    } else {
      setGuestNamesInput("");
    }
  }, [editingBooking]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formVisible, setFormVisible] = useState(false);
  const [totalCost, setTotalCost] = useState<number | null>(null);
  const [bookingDescription, setBookingDescription] = useState<string | null>(
    order.bookingDescription || null
  );
  const [chaletType, setChaletType] = useState<"big" | "small" | null>(null); // Add state for chaletType
  const [chaletId, setChaletId] = useState<number | null>(null); // Add state for chaletId

  const [isBookingUpdated, setIsBookingUpdated] = useState(false);
  // Ref to top of modal content to allow scroll-to-top behavior
  const topRef = useRef<HTMLDivElement | null>(null);
  // Snapshot of booking before last update for Undo capability
  const [prevBookingSnapshot, setPrevBookingSnapshot] = useState<BookingDetailsDTO | null>(null);
  // Track changed fields for highlight after update
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());

  const handleEditBooking = (booking: BookingDetailsDTO) => {
    setEditingBooking(booking);
    console.log("Booking from handleEditBooking:", booking);

    setArrivalDate(format(new Date(booking.arrivalDate), "yyyy-MM-dd"));
    setDepartureDate(format(new Date(booking.departureDate), "yyyy-MM-dd"));
    setBookingDescription(order.bookingDescription || ""); // Ensure bookingDescription is set correctly

    setGuestNamesInput(booking.guestNames ? booking.guestNames.join(", ") : "");
    setTotalCost(booking.totalCost); // Ensure totalCost is set correctly
    setChaletType(booking.chaletType as "big" | "small" | null); // Ensure chaletType is set correctly
    setChaletId(booking.chaletId || null); // Ensure chaletId is set correctly
    // Capture snapshot for potential Undo before any modifications
    setPrevBookingSnapshot(JSON.parse(JSON.stringify(booking)));
    // Scroll to top for clarity when entering edit mode
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 0);
  };

  const handleUpdateBooking = async () => {
    if (!editingBooking) return;

    const updateRequest: BookingUpdateRequest = {
      bookingDescription:
        bookingDescription !== null ? bookingDescription : undefined,
      totalPrice: totalCost !== null ? totalCost : undefined, // Add totalPrice to the payload
      chaletType: chaletType !== null ? chaletType : undefined, // Add chaletType to the payload
      chaletId: chaletId !== null ? chaletId : undefined, // Add chaletId to the payload
      guestNames: guestNamesInput
        .split(",")
        .map((name) => name.trim())
        .filter((name) => name.length > 0),
      arrivalDate: arrivalDate || undefined,
      departureDate: departureDate || undefined,
    };

    try {
      const result = await updateBooking(editingBooking.bookingId, updateRequest);
      const updated = result.updatedBooking;
      // Optimistically merge updated booking into local order.bookings array
      const target = order.bookings.find(b => b.bookingId === updated.bookingId);
      if (target) {
        target.arrivalDate = updated.arrivalDate;
        target.departureDate = updated.departureDate;
        target.bookingDescription = updated.bookingDescription;
        target.totalCost = updated.totalCost;
        target.guestNames = updated.guestNames;
        target.chaletType = updated.chaletType;
        target.chaletId = updated.chaletId;
      }
      // Update local state vars for immediate display
      setArrivalDate(format(new Date(updated.arrivalDate), "yyyy-MM-dd"));
      setDepartureDate(format(new Date(updated.departureDate), "yyyy-MM-dd"));
  setBookingDescription(updated.bookingDescription ?? null);
  setTotalCost(typeof updated.totalCost === 'number' ? updated.totalCost : null);
      setGuestNamesInput(updated.guestNames ? updated.guestNames.join(", ") : "");
      setChaletType(updated.chaletType as ("big"|"small") | null);
      setChaletId(updated.chaletId || null);
      setEditingBooking(null); // exit edit mode
      setFormVisible(false);
      setIsBookingUpdated(true);
      setUpdatedGuestNames(updated.guestNames ? updated.guestNames.join(", ") : null);

      // Determine changed fields using snapshot vs updated
      if (prevBookingSnapshot) {
        const cf = new Set<string>();
        const compare = (field: string, prevVal: any, newVal: any) => {
          if (prevVal === undefined || newVal === undefined) {
            if (prevVal !== newVal) cf.add(field);
            return;
          }
          if (Array.isArray(prevVal) && Array.isArray(newVal)) {
            if (prevVal.join('|') !== newVal.join('|')) cf.add(field);
          } else if (prevVal !== newVal) {
            cf.add(field);
          }
        };
        compare('arrivalDate', prevBookingSnapshot.arrivalDate, updated.arrivalDate);
        compare('departureDate', prevBookingSnapshot.departureDate, updated.departureDate);
        compare('bookingDescription', prevBookingSnapshot.bookingDescription, updated.bookingDescription);
        compare('totalCost', prevBookingSnapshot.totalCost, updated.totalCost);
        compare('guestNames', prevBookingSnapshot.guestNames, updated.guestNames);
        compare('chaletType', prevBookingSnapshot.chaletType, updated.chaletType);
        compare('chaletId', prevBookingSnapshot.chaletId, updated.chaletId);
        setChangedFields(cf);
      }

      showToast("Booking updated successfully", "success");
      // Kick off refresh for authoritative data
      fetchBookings();
      onUpdate && onUpdate();
      // Scroll to top to show updated summary clearly
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 0);
    } catch (error: any) {
      console.error("Error updating booking:", error);
      
      // Extract detailed error message from API response
      let errorMessage = "Failed to update booking";
      
      if (error?.response?.data) {
        const data = error.response.data;
        // Check for various error message formats
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (data.errors) {
          // Handle validation errors
          const errors = Object.values(data.errors).flat();
          errorMessage = errors.join(', ');
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Add specific handling for common scenarios
      if (errorMessage.toLowerCase().includes('available') || 
          errorMessage.toLowerCase().includes('chalet')) {
        showToast(`Cannot update: ${errorMessage}. Please choose different dates or chalet.`, "error", 8000);
      } else if (error?.response?.status === 409) {
        showToast(`Conflict: ${errorMessage}. The requested chalet may not be available for these dates.`, "error", 8000);
      } else if (error?.response?.status === 400) {
        showToast(`Invalid request: ${errorMessage}`, "error", 7000);
      } else {
        showToast(`Update failed: ${errorMessage}`, "error", 6000);
      }
    }
  };

  const handleUndoBookingUpdate = async () => {
    if (!prevBookingSnapshot) return;
    const snap = prevBookingSnapshot;
    const undoRequest: BookingUpdateRequest = {
      bookingDescription: snap.bookingDescription || undefined,
      totalPrice: typeof snap.totalCost === 'number' ? snap.totalCost : undefined,
      chaletType: (snap.chaletType as ("big"|"small")) || undefined,
      chaletId: snap.chaletId || undefined,
      guestNames: snap.guestNames || undefined,
      arrivalDate: snap.arrivalDate || undefined,
      departureDate: snap.departureDate || undefined,
    } as any; // extend with guestNames
    try {
      const result = await updateBooking(snap.bookingId, undoRequest);
      const restored = result.updatedBooking;
      const target = order.bookings.find(b => b.bookingId === restored.bookingId);
      if (target) {
        target.arrivalDate = restored.arrivalDate;
        target.departureDate = restored.departureDate;
        target.bookingDescription = restored.bookingDescription;
        target.totalCost = restored.totalCost;
        target.guestNames = restored.guestNames;
        target.chaletType = restored.chaletType;
        target.chaletId = restored.chaletId;
      }
      setArrivalDate(format(new Date(restored.arrivalDate), "yyyy-MM-dd"));
      setDepartureDate(format(new Date(restored.departureDate), "yyyy-MM-dd"));
      setBookingDescription(restored.bookingDescription ?? null);
      setTotalCost(typeof restored.totalCost === 'number' ? restored.totalCost : null);
      setGuestNamesInput(restored.guestNames ? restored.guestNames.join(', ') : '');
      setChaletType(restored.chaletType as ("big"|"small") | null);
      setChaletId(restored.chaletId || null);
      setIsBookingUpdated(false);
      setChangedFields(new Set());
      setPrevBookingSnapshot(null); // Clear snapshot after undo
      showToast('Changes reverted successfully', 'success');
      fetchBookings();
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    } catch (err: any) {
      console.error('Undo failed:', err);
      const errorMsg = err?.response?.data?.message || err?.message || 'Unknown error';
      showToast(`Failed to undo changes: ${errorMsg}`, 'error', 6000);
    }
  };

  //console.log("order", order, order.bookings);

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const handleDeleteBooking = async (bookingId: number) => {
    setPendingDeleteId(bookingId);
    showToast("Delete booking? Click 'Confirm Delete' button or wait to cancel.", "info", 10000);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      console.log(`Deleting booking with ID: ${pendingDeleteId}`);
      await deleteBooking(pendingDeleteId);
      showToast("Booking deleted successfully", "success");
      fetchBookings(); // Refresh the bookings list
      setPendingDeleteId(null);
      onClose && onClose();
    } catch (error: any) {
      console.error("Error deleting booking:", error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      if (error?.response?.status === 401) {
        showToast("Unauthorized: Please check your login", "error");
      } else {
        showToast(`Failed to delete booking: ${errorMsg}`, "error", 6000);
      }
      setPendingDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setPendingDeleteId(null);
    showToast("Deletion cancelled", "info");
  };

  const transaction =
    booking?.transactions && booking?.transactions.length > 0
      ? booking?.transactions[0]
      : null;
  const formattedAmount = transaction
    ? new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(transaction.amount / 100)
    : null;

  const getChaletTypeLabel = (type: "big" | "small" | null) => {
    return type === "big" ? "Big" : type === "small" ? "Small" : "";
  };

  const getChaletTypeFromString = (type: string | null) => {
    if (type === "Big") return "big";
    if (type === "Small") return "small";
    return null;
  };

  return (
    <>
  <div ref={topRef}></div>
  <section className="booking-customer-details-section">
        {order.bookings.map((booking) => (
          <div key={booking.bookingId} className="booking-detail">
            {booking.mainImageUrl && (
              <div className="booking-banner">
                <img src={booking.mainImageUrl} alt="Booking" />
              </div>
            )}
            <div className="booking-info">
              <h2>Booking ID: {booking.bookingId}</h2>
              <p>
                <strong>Order Number:</strong> {order.orderNumber}
              </p>
              <p style={changedFields.has('arrivalDate') ? { backgroundColor: '#fff3cd', borderRadius: 4, padding: '2px 6px' } : undefined}>
                <strong>Arrival Date:</strong>{' '}
                {editingBooking && editingBooking.bookingId === booking.bookingId ? (
                  <input
                    type="date"
                    value={arrivalDate || ""}
                    onChange={(e) => setArrivalDate(e.target.value)}
                  />
                ) : (
                  // If updated locally, reflect the new value immediately
                  isBookingUpdated
                    ? (arrivalDate ?? format(new Date(booking.arrivalDate), "yyyy-MM-dd"))
                    : format(new Date(booking.arrivalDate), "yyyy-MM-dd")
                )}
              </p>
              <p style={changedFields.has('departureDate') ? { backgroundColor: '#fff3cd', borderRadius: 4, padding: '2px 6px' } : undefined}>
                <strong>Departure Date:</strong>{' '}
                {editingBooking && editingBooking.bookingId === booking.bookingId ? (
                  <input
                    type="date"
                    value={departureDate || ""}
                    onChange={(e) => setDepartureDate(e.target.value)}
                  />
                ) : (
                  isBookingUpdated
                    ? (departureDate ?? format(new Date(booking.departureDate), "yyyy-MM-dd"))
                    : format(new Date(booking.departureDate), "yyyy-MM-dd")
                )}
              </p>
              <p>
                <strong>Customer Name:</strong> {booking.customerName}
              </p>
              {booking.guestNames && booking.guestNames.length > 0 && (
                <p>
                  <strong>Guest Names:</strong> {booking.guestNames.join(", ")}
                </p>
              )}
              {booking.companyName && (
                <p>
                  <strong>Company Name:</strong> {booking.companyName}
                </p>
              )}{" "}
              {booking.preBooking && (
                <p>
                  <strong>Pre Booking:</strong>{" "}
                  {booking.preBooking ? "Yes" : "No"}
                </p>
              )}
              <p style={changedFields.has('totalCost') ? { backgroundColor: '#fff3cd', borderRadius: 4, padding: '2px 6px' } : undefined}>
                <strong>Total Cost:</strong>{' '}
                {editingBooking &&
                editingBooking.bookingId === booking.bookingId ? (
                  <input
                    type="number"
                    value={totalCost !== null ? totalCost : ""}
                    onChange={(e) => setTotalCost(parseFloat(e.target.value))}
                  />
                ) : (
                  (() => {
                    const cost = isBookingUpdated
                      ? totalCost ?? 0
                      : booking.totalCost;
                    //   console.log("Total Cost:", cost); // debug only
                    return new Intl.NumberFormat("de-DE", {
                      style: "currency",
                      currency: "EUR",
                    }).format(cost);
                  })()
                )}
              </p>
              <p>
                <strong>Package Name:</strong> {booking.packageName}
              </p>
              <p>
                {booking.packageName !== "Outside Package" ? (
                  <div>
                    <strong>Pricing Option:</strong>{" "}
                    {booking.pricingModelDescription}
                  </div>
                ) : null}
              </p>
              <p>
                <strong>Booking Description:</strong>
              </p>
              <div className="booking-description__area">
                {editingBooking &&
                editingBooking.bookingId === booking.bookingId ? (
                  <>
                    <textarea
                      value={bookingDescription || ""}
                      onChange={(e) => setBookingDescription(e.target.value)}
                    />
                    <select
                      value={chaletType || ""}
                      onChange={(e) =>
                        setChaletType(e.target.value as "big" | "small")
                      }
                    >
                      <option value={undefined}>Select Chalet Type</option>
                      <option value="big">Big</option>
                      <option value="small">Small</option>
                    </select>
                    <p></p>
                    {chaletType &&
                      (chaletType === "big" || chaletType === "small") && (
                        <AvailableChalets
                          chaletType={chaletType}
                          startDate={arrivalDate}
                          endDate={departureDate}
                          onSelectChalet={(id) => setChaletId(id)}
                          selectedChaletId={chaletId}
                        />
                      )}

                    {chaletType && (
                      <p>
                        <strong>Selected Chalet Type:</strong> {chaletType}
                      </p>
                    )}
                    {chaletId && (
                      <p>
                        <strong>Selected Chalet ID:</strong> {chaletId}
                      </p>
                    )}
                    <p>
                      <strong>Guest Names:</strong>{" "}
                      {isBookingUpdated
                        ? guestNamesInput
                        : booking.guestNames && booking.guestNames.length > 0
                        ? booking.guestNames.join(", ")
                        : ""}
                      <textarea
                        value={guestNamesInput}
                        onChange={(e) => setGuestNamesInput(e.target.value)}
                        rows={2}
                        style={{ width: "100%" }}
                      />
                    </p>
                  </>
                ) : (
                  <>
                    <span style={changedFields.has('bookingDescription') ? { backgroundColor: '#fff3cd', borderRadius: 4, padding: '2px 6px' } : undefined}>
                      {isBookingUpdated
                        ? bookingDescription
                        : order.bookingDescription}
                    </span>

                    <p style={changedFields.has('guestNames') ? { backgroundColor: '#fff3cd', borderRadius: 4, padding: '2px 6px' } : undefined}>
                      <strong>Guest Names:</strong>{' '}
                      {isBookingUpdated
                        ? updatedGuestNames
                        : booking.guestNames && booking.guestNames.length > 0
                        ? booking.guestNames.join(", ")
                        : ""}
                    </p>
                    <p style={changedFields.has('chaletType') ? { backgroundColor: '#fff3cd', borderRadius: 4, padding: '2px 6px' } : undefined}>
                      <strong>Chalet Type:</strong>{' '}
                      {isBookingUpdated
                        ? chaletType
                        : booking.chaletType
                        ? getChaletTypeLabel(
                            getChaletTypeFromString(booking.chaletType)
                          )
                        : ""}
                    </p>
                    <p style={changedFields.has('chaletId') ? { backgroundColor: '#fff3cd', borderRadius: 4, padding: '2px 6px' } : undefined}>
                      <strong>Chalet ID:</strong>{' '}
                      {isBookingUpdated ? chaletId : booking.chaletId}
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="customer-details">
              <h5>Customer Information</h5>
              <p>
                <strong>Address:</strong> {booking.address}
              </p>
              {booking.address2 && (
                <p>
                  <strong>Address 2:</strong> {booking.address2}
                </p>
              )}
              <p>
                <strong>City:</strong> {booking.city}
              </p>
              <p>
                <strong>Zip Code:</strong> {booking.zipCode}
              </p>
              <p>
                <strong>Country:</strong> {booking.country}
              </p>
              <p>
                <strong>Phone:</strong> {booking.phone}
              </p>
              <p>
                <strong>Email:</strong> {booking.email}
              </p>
            </div>

            {transaction && (
              <div className="transaction-details">
                <h5>Transaction Details</h5>
                <p>
                  <strong>Amount:</strong> {formattedAmount}
                </p>
                {transaction.receiptUrl && (
                  <p>
                    <strong>Receipt:</strong>{" "}
                    <a
                      href={transaction.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Receipt
                    </a>
                  </p>
                )}
              </div>
            )}

            <div className="booking-actions" style={{ textAlign: "right" }}>
              {editingBooking &&
              editingBooking.bookingId === booking.bookingId ? (
                <button
                  className="btn btn-sm btn-outline-success me-2"
                  onClick={handleUpdateBooking}
                  title="Save changes"
                >
                  <i className="fas fa-save"></i> Save
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => handleEditBooking(booking)}
                    title="Edit booking"
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                  {prevBookingSnapshot && changedFields.size > 0 && !editingBooking && (
                    <button
                      className="btn btn-sm btn-outline-warning me-2"
                      onClick={handleUndoBookingUpdate}
                      title="Undo last changes"
                    >
                      <i className="fas fa-undo"></i> Undo
                    </button>
                  )}
                </>
              )}
              {pendingDeleteId === booking.bookingId ? (
                <div style={{ display: 'inline-flex', gap: '4px' }}>
                  <button
                    className="btn btn-sm btn-danger me-1"
                    onClick={confirmDelete}
                  >
                    <i className="fas fa-check"></i> Confirm Delete
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={cancelDelete}
                  >
                    <i className="fas fa-times"></i> Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-sm btn-outline-danger me-2"
                  onClick={() => handleDeleteBooking(booking.bookingId)}
                >
                  <i className="fas fa-trash-alt"></i> Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </section>
    </>
  );
};

export default CalendarViewManageBookingDetails;
